/**
 * Core Poker Engine - Pure business logic for Texas Hold'em
 * All methods are static and return new state objects (immutable)
 */

import type {
  Card,
  GameConfig,
  GameState,
  EnginePlayer,
  ActionType,
  PlayerAction,
  ActionResult,
  BoardState,
  HandOutcome,
  PlayerPosition
} from './types'
import { createDeck, shuffle, dealCards } from './deck'
import { validateAction, getAvailableActions } from './validation'
import {
  calculateSidepots,
  getTotalPotAmount,
  distributePots,
  mergePotStructures,
  extractUncalledBets
} from './sidepot'
import {
  getFirstPlayerToAct,
  getNextActivePlayer,
  findNextActiveSeat,
  findNextBlindSeat,
  setNotActedThisRoundOtherPlayers,
  getGameStateFlow
} from './progression'

/**
 * Blind-posting policy.
 *
 * - `'skip'` (default for cash games): sitting-out players are skipped when
 *   selecting blind seats — they pay nothing, the next active player pays.
 *   This matches the standard cash-game expectation (a "missed-blinds" ledger
 *   handled outside the engine).
 * - `'force'` (tournaments): sitting-out players pay blinds when their seat
 *   passes the button. They still don't get dealt cards or act, but their
 *   chips are debited and marked all-in if the blind is greater than their
 *   stack so sidepots remain correct.
 */
export type BlindPolicy = 'force' | 'skip'

/**
 * Compute the position + blind-posting patch for a single player at
 * `startHand` time. Behavior depends on `blindPolicy`:
 *
 * - `'skip'` (cash): only active players post blinds. Sitting-out players in a
 *   "blind seat" by index are a no-op because seat selection already skipped
 *   them via `findNextActiveSeat`.
 * - `'force'` (tournament): sitting-out players in a blind seat pay the blind
 *   from their stack. If the blind drains them, they are marked `'allin'` so
 *   sidepot eligibility is correct — otherwise their contribution would be in
 *   the pot with no winner eligible to claim it.
 */
function applyStartHandPosition(
  player: EnginePlayer,
  ctx: {
    buttonSeat: number
    smallBlindSeat: number
    bigBlindSeat: number
    smallBlind: number
    bigBlind: number
    blindPolicy: BlindPolicy
  }
): EnginePlayer {
  // Who is allowed to post a blind from their stack in this hand.
  // Under `'skip'`, only active players post. Under `'force'`, sittingout
  // players also post (no cards, no action, but chips are debited).
  const canPostBlind =
    player.chips > 0 &&
    player.state !== 'out' &&
    player.state !== 'awaiting_rebuy' &&
    (player.state === 'active' || ctx.blindPolicy === 'force')

  const atSmallBlind = player.seatId === ctx.smallBlindSeat && canPostBlind
  const atBigBlind = player.seatId === ctx.bigBlindSeat && canPostBlind
  const blindAmount = atBigBlind ? ctx.bigBlind : atSmallBlind ? ctx.smallBlind : 0

  // Non-active player who's not in a blind seat: nothing to do.
  if (player.state !== 'active' && blindAmount === 0) return player

  // Position only applies to active players (sittingout stays invisible).
  let position: PlayerPosition = player.position
  if (player.state === 'active') {
    position = atSmallBlind
      ? 'sb'
      : atBigBlind
        ? 'bb'
        : player.seatId === ctx.buttonSeat
          ? 'd'
          : 'none'
  }

  if (blindAmount === 0) return { ...player, position }

  const posted = Math.min(blindAmount, player.chips)
  // If the blind drains the player's stack, mark them all-in regardless of
  // their prior state so sidepot eligibility reflects the contribution. A
  // sittingout player drained by a blind is treated as all-in in the pot
  // even though they aren't dealt cards — `completeHand`'s sidepot
  // distribution will then list them as eligible for the level they funded.
  const drainedByBlind = posted === player.chips
  const nextState: EnginePlayer['state'] = drainedByBlind ? 'allin' : player.state

  return {
    ...player,
    position,
    chips: player.chips - posted,
    currentBet: posted,
    state: nextState,
    // Only an `active` BB gets to act preflop. A sittingout BB stays out
    // of the action loop.
    hasActedThisRound: atBigBlind && player.state === 'active' ? false : player.hasActedThisRound
  }
}

export class PokerEngine {
  private static getStateForHandStart(player: EnginePlayer): EnginePlayer['state'] {
    switch (player.state) {
      case 'sittingout':
      case 'awaiting_rebuy':
        return player.state
      default:
        return player.chips > 0 ? 'active' : 'out'
    }
  }

  /**
   * Create initial game state
   * @param config - Game configuration
   * @param playerCount - Number of players
   * @returns Initial game state
   */
  static createGame(config: GameConfig, playerCount: number): GameState {
    if (playerCount < 2 || playerCount > config.maxPlayers) {
      throw new Error(`Player count must be between 2 and ${config.maxPlayers}`)
    }

    const players: EnginePlayer[] = []

    // Create placeholder players (will be populated when joining)
    for (let i = 0; i < playerCount; i++) {
      players.push({
        id: `player-${i}`,
        userId: `user-${i}`,
        name: `Player ${i + 1}`,
        chips: config.startingChips,
        currentBet: 0,
        cards: [],
        state: 'waiting',
        hasActedThisRound: false,
        seatId: i,
        position: 'none'
      })
    }

    return {
      boardState: 'predeal',
      communityCards: [],
      deck: [],
      pots: [
        {
          amount: 0,
          eligiblePlayerIds: [],
          contributions: {},
          cumulativeLevel: 0
        }
      ],
      players,
      currentPlayerId: null,
      button: 0,
      handNumber: 0,
      bigBlind: config.bigBlind,
      smallBlind: config.smallBlind
    }
  }

  /**
   * Rotate the dealer button clockwise to the next active player
   *
   * In poker, the dealer button moves clockwise after each hand completes.
   * Call this method between `completeHand()` and `startHand()` to advance
   * the button position for the next hand.
   *
   * @example
   * ```typescript
   * let state = createGame(config, 6)
   * state = startHand(state)           // First hand (button at seat 0)
   * // ... play hand ...
   * completeHand(state)
   *
   * state = rotateButton(state)        // Move button clockwise
   * state = startHand(state)           // Next hand with new button position
   * ```
   *
   * @param state - Current game state
   * @returns New state with button advanced to next active seat (clockwise)
   */
  static rotateButton(state: GameState): GameState {
    const nextButton = findNextActiveSeat(state, state.button)

    return {
      ...state,
      button: nextButton
    }
  }

  /**
   * Start a new hand
   * - Shuffles deck (or uses provided deck for deterministic testing)
   * - Deals hole cards
   * - Posts blinds
   * - Sets first player to act
   *
   * Note: This method uses the current `state.button` position.
   * Call `rotateButton()` before this method to advance the button for subsequent hands.
   *
   * @param state - Current game state
   * @param options - Optional configuration
   * @param options.deck - Pre-ordered deck to use instead of shuffling (for deterministic testing)
   * @param options.blindPolicy - How sitting-out players are treated when their
   *   seat lands on a blind position. `'skip'` (default) skips them and the
   *   next active player pays. `'force'` debits chips from sitting-out players
   *   (standard tournament rules). See {@link BlindPolicy}.
   * @returns New game state with hand started
   */
  static startHand(
    state: GameState,
    options?: { deck?: Card[]; blindPolicy?: BlindPolicy } | (() => number)
  ): GameState {
    const deck =
      typeof options === 'function'
        ? shuffle(createDeck(), options)
        : options?.deck ?? shuffle(createDeck())
    const blindPolicy: BlindPolicy =
      typeof options === 'function' ? 'skip' : options?.blindPolicy ?? 'skip'

    // Phase 1: Reset players, deal cards, and count active players in a single pass
    let remainingDeck = deck
    let activeCount = 0
    const preparedPlayers = state.players.map((p) => {
      // Reset player for new hand (preserve sittingout - don't deal cards)
      const resetPlayer: EnginePlayer = {
        ...p,
        currentBet: 0,
        cards: [],
        state: PokerEngine.getStateForHandStart(p),
        hasActedThisRound: false,
        position: 'none' as const
      }

      // Deal cards to active players
      if (resetPlayer.state === 'active') {
        activeCount++
        const { cards, remaining } = dealCards(remainingDeck, 2)
        remainingDeck = remaining
        return { ...resetPlayer, cards }
      }

      return resetPlayer
    })

    // Phase 2: Calculate blind positions.
    //
    // SB-IS-Button heads-up only applies when exactly 2 players are dealt
    // in. Seat selection respects `blindPolicy`:
    // - `'force'` (tournament): `findNextBlindSeat` includes sitting-out
    //   players so they pay blinds when the button passes their seat.
    // - `'skip'` (cash, default): `findNextActiveSeat` skips them entirely;
    //   the next active player pays.
    const isHeadsUp = activeCount === 2
    const buttonSeat = state.button
    const tempState: GameState = { ...state, players: preparedPlayers }
    const findBlindSeat = blindPolicy === 'force' ? findNextBlindSeat : findNextActiveSeat

    const smallBlindSeat = isHeadsUp ? buttonSeat : findBlindSeat(tempState, buttonSeat)
    const bigBlindSeat = isHeadsUp
      ? findBlindSeat(tempState, buttonSeat)
      : findBlindSeat(tempState, smallBlindSeat)

    // Phase 3: Apply position + blind to each player. Pot stays empty —
    // blind chips live in `currentBet` until `progressToNextRound`.
    const updatedPlayers = preparedPlayers.map((player) =>
      applyStartHandPosition(player, {
        buttonSeat,
        smallBlindSeat,
        bigBlindSeat,
        smallBlind: state.smallBlind,
        bigBlind: state.bigBlind,
        blindPolicy
      })
    )

    // CRITICAL: Pot starts EMPTY - blind chips stay in currentBet until progressToNextRound()
    // This prevents double-counting chips (once in currentBet, once in pot)
    const newState: GameState = {
      ...state,
      boardState: 'deal',
      communityCards: [],
      deck: remainingDeck,
      pots: [
        {
          amount: 0, // Start empty - blinds are in currentBet, will move to pot after first round
          eligiblePlayerIds: [],
          contributions: {},
          cumulativeLevel: 0
        }
      ],
      players: updatedPlayers,
      handNumber: state.handNumber + 1,
      currentPlayerId: null, // Will be set by getFirstPlayerToAct
      button: state.button // Keep button unchanged (use rotateButton() to advance)
    }

    // Set first player to act (player after big blind)
    const firstPlayer = getFirstPlayerToAct(newState)
    return { ...newState, currentPlayerId: firstPlayer }
  }

  /**
   * Execute a player action
   * @param state - Current game state
   * @param playerId - Player ID
   * @param action - Player action (discriminated union)
   * @returns Action result with new state
   */
  static executeAction(state: GameState, playerId: string, action: PlayerAction): ActionResult {
    // Validate action
    const validation = validateAction(state, playerId, action)
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error,
        state
      }
    }

    // Player existence is guaranteed by validateAction check above
    // biome-ignore lint/style/noNonNullAssertion: player exists after validation
    const player = state.players.find((p) => p.id === playerId)!
    const otherPlayers = state.players.filter((p) => p.id !== playerId)
    const blindsPosted = state.boardState === 'deal' && state.players.some((p) => p.currentBet > 0)
    const highestBet = Math.max(blindsPosted ? state.bigBlind : 0, ...otherPlayers.map((p) => p.currentBet))

    const newState = { ...state }
    const updatedPlayer = { ...player, hasActedThisRound: true }

    // Execute action
    switch (action.type) {
      case 'fold':
        updatedPlayer.state = 'folded'
        // Folded players are excluded from pot eligibility during distribution
        // (handled by distributePots() > findWinnersForPot based on player state)

        break

      case 'check':
        // No bet change, just mark as acted
        break

      case 'call': {
        const callAmount = Math.min(highestBet - player.currentBet, player.chips)
        updatedPlayer.chips -= callAmount
        updatedPlayer.currentBet += callAmount
        // Chips stay in front of player until round completes (real poker)
        // Pots calculated in progressToNextRound()

        if (updatedPlayer.chips === 0) {
          updatedPlayer.state = 'allin'
        }
        break
      }

      case 'bet': {
        const betAmount = Math.min(action.amount, player.chips)
        updatedPlayer.chips -= betAmount
        updatedPlayer.currentBet += betAmount
        // Chips stay in front of player until round completes (real poker)
        // Pots calculated in progressToNextRound()

        if (updatedPlayer.chips === 0) {
          updatedPlayer.state = 'allin'
        }

        // Mark all other active players as not acted (they need to respond to bet)
        newState.players = setNotActedThisRoundOtherPlayers(newState.players, updatedPlayer, 'active')
        break
      }

      case 'raise': {
        const raiseAmount = Math.min(action.amount, player.chips)
        updatedPlayer.chips -= raiseAmount
        updatedPlayer.currentBet += raiseAmount
        // Chips stay in front of player until round completes (real poker)
        // Pots calculated in progressToNextRound()

        if (updatedPlayer.chips === 0) {
          updatedPlayer.state = 'allin'
        }

        // Mark all other active players as not acted (they need to respond to raise)
        newState.players = setNotActedThisRoundOtherPlayers(newState.players, updatedPlayer, 'active')
        break
      }

      case 'allin': {
        const allinAmount = player.chips
        updatedPlayer.chips = 0
        updatedPlayer.currentBet += allinAmount
        updatedPlayer.state = 'allin'
        // Chips stay in front of player until round completes (real poker)
        // Pots calculated in progressToNextRound()

        // Mark all other active players as not acted (they need to respond to all-in)
        newState.players = setNotActedThisRoundOtherPlayers(newState.players, updatedPlayer, 'active')
        break
      }
    }

    // Update player in state
    const newStateUpdatedPlayers = newState.players.map((p) => (p.id === playerId ? updatedPlayer : p))
    newState.players = newStateUpdatedPlayers

    // Side pots are now calculated in progressToNextRound() after betting round completes
    // This matches real poker where pots are finalized between rounds, not during betting

    // Check if round/hand is complete BEFORE setting currentPlayerId
    const flowState = getGameStateFlow({ ...newState })
    const roundComplete = flowState === 'complete_round'
    const handComplete = flowState === 'complete_hand'
    const areAllPlayerAllIn = flowState === 'all_players_all_in'

    // Set currentPlayerId based on whether round is complete
    // When round/hand is complete, currentPlayerId should be null until progressToNextRound() is called
    if (roundComplete || handComplete || areAllPlayerAllIn) {
      newState.currentPlayerId = null
    } else {
      // Determine next player
      const currentPlayerSeat = updatedPlayer.seatId
      const nextPlayer = getNextActivePlayer(newState, currentPlayerSeat)
      newState.currentPlayerId = nextPlayer
    }

    return {
      success: true,
      state: newState,
      flow: flowState
    }
  }

  /**
   * Progress to next betting round
   * deal → flop → turn → river → showdown
   * @param state - Current game state
   * @returns New game state in next round
   */
  static progressToNextRound(state: GameState): GameState {
    const boardStateProgression: Record<BoardState, BoardState> = {
      predeal: 'deal',
      deal: 'flop',
      flop: 'turn',
      turn: 'river',
      river: 'showdown',
      showdown: 'showdown', // Stay at showdown,
      hand_complete: 'hand_complete' // Stay at hand_complete
    }

    const nextBoardState = boardStateProgression[state.boardState]

    // Deal community cards based on next state
    let { deck, communityCards } = state

    if (nextBoardState === 'flop') {
      // Burn 1, deal 3
      const { cards, remaining } = dealCards(deck.slice(1), 3)
      communityCards = cards
      deck = remaining
    } else if (nextBoardState === 'turn') {
      // Burn 1, deal 1
      const { cards, remaining } = dealCards(deck.slice(1), 1)
      communityCards = [...state.communityCards, ...cards]
      deck = remaining
    } else if (nextBoardState === 'river') {
      // Burn 1, deal 1
      const { cards, remaining } = dealCards(deck.slice(1), 1)
      communityCards = [...state.communityCards, ...cards]
      deck = remaining
    }

    // Calculate pots from current round's bets BEFORE resetting currentBet
    // In real poker, chips are collected into pots between betting rounds
    const hasActiveBets = state.players.some((p) => p.currentBet > 0)

    let newPots = state.pots
    let uncalledBets: Record<string, number> = {}
    if (hasActiveBets) {
      // Calculate pot structure from THIS round's betting
      const roundPots = calculateSidepots(state.players)
      // Merge with existing pots (accumulate chips across rounds)
      // Pass players to correctly identify all-in levels for sidepot creation
      newPots = mergePotStructures(state.pots, roundPots, state.players)

      // Return uncalled bets — pots where only 1 player is eligible (excess over
      // what anyone else could match). In real poker the dealer pushes these back.
      const extracted = extractUncalledBets(newPots)
      newPots = extracted.pots
      uncalledBets = extracted.uncalledBets
    }

    // Reset all players' action state for new round and return uncalled bets
    const players = state.players.map((p) => ({
      ...p,
      chips: p.chips + (uncalledBets[p.id] || 0),
      hasActedThisRound: false,
      currentBet: 0 // Reset bets for new round
    }))

    const newState: GameState = {
      ...state,
      boardState: nextBoardState,
      communityCards,
      deck,
      players,
      currentPlayerId: null,
      pots: newPots // Use calculated pots from this betting round
    }

    // At showdown, no player acts - hand is complete, cards are revealed
    if (nextBoardState === 'showdown') {
      return newState
    }

    // Set first player to act (player after button for postflop)
    const firstPlayer = getFirstPlayerToAct(newState)
    return { ...newState, currentPlayerId: firstPlayer }
  }

  /**
   * Complete hand and determine winners
   * @param state - Current game state
   * @returns Hand outcome (discriminated union: win_by_fold or showdown)
   */
  static completeHand(state: GameState): HandOutcome {
    // Only count players actually in the hand (exclude 'waiting' and 'sittingout' who are not in current hand)
    const nonFoldedPlayers = state.players.filter(
      (p) =>
        p.state !== 'folded' &&
        p.state !== 'out' &&
        p.state !== 'waiting' &&
        p.state !== 'sittingout' &&
        p.state !== 'awaiting_rebuy'
    )

    // CRITICAL FIX: If players still have currentBet > 0 (hand completed before progressToNextRound),
    // we need to calculate pots from those bets FIRST before distributing
    const hasUnresolvedBets = state.players.some((p) => p.currentBet > 0)
    let finalPots = state.pots

    if (hasUnresolvedBets) {
      // Calculate pots from current bets
      const roundPots = calculateSidepots(state.players)
      // Merge with existing pots (if any were accumulated from previous rounds)
      // Pass players to correctly identify all-in levels for sidepot creation
      finalPots = mergePotStructures(state.pots, roundPots, state.players)
    }

    // Determine total pot amount (use sidepots if available, otherwise legacy pot)
    const totalPot = getTotalPotAmount(finalPots)

    // Win by fold case - no hand evaluation needed
    if (nonFoldedPlayers.length === 1) {
      const winner = nonFoldedPlayers[0]

      // Use distributePots for proper pot distribution (handles sidepot eligibility)
      const potDistribution = distributePots(finalPots, nonFoldedPlayers, state.communityCards)

      return {
        type: 'win_by_fold', // Discriminator
        winner: {
          playerId: winner.id,
          userId: winner.userId,
          playerName: winner.name,
          amount: totalPot, // Winner gets all pots
          cards: winner.cards
          // No handRank property!
        },
        pot: totalPot,
        communityCards: state.communityCards,
        potDistribution // Include detailed pot-by-pot distribution
      }
    }

    // Showdown case - evaluate hands
    // Ensure pots have eligiblePlayerIds set (for backward compatibility with tests)
    // If first pot (main pot) has no eligiblePlayerIds, set it to all non-folded players
    let pots = finalPots
    if (pots.length > 0 && pots[0].eligiblePlayerIds.length === 0) {
      pots = [
        {
          ...pots[0],
          eligiblePlayerIds: nonFoldedPlayers.map((p) => p.id)
        },
        ...pots.slice(1)
      ]
    }

    // Use distributePots to properly handle sidepot eligibility
    const potDistribution = distributePots(pots, nonFoldedPlayers, state.communityCards)

    // Flatten all pot winners into a single winners array with total amounts per player
    const winnerAmounts = new Map<string, number>()
    const winnerDetails = new Map<string, { userId: string; playerName: string; hand: any }>()

    for (const potWinner of potDistribution.potWinners) {
      for (const winner of potWinner.winners) {
        const current = winnerAmounts.get(winner.playerId) || 0
        winnerAmounts.set(winner.playerId, current + winner.amount)

        // Store winner details (hand info from first pot they won)
        if (!winnerDetails.has(winner.playerId)) {
          winnerDetails.set(winner.playerId, {
            userId: winner.userId,
            playerName: winner.playerName,
            // biome-ignore lint/style/noNonNullAssertion: <explanation>
            hand: winner.hand!
          })
        }
      }
    }

    // Convert to final winners array
    const winners = Array.from(winnerAmounts.entries()).map(([playerId, amount]) => {
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      const details = winnerDetails.get(playerId)!
      return {
        playerId,
        userId: details.userId,
        playerName: details.playerName,
        amount,
        hand: details.hand
      }
    })

    return {
      type: 'showdown',
      winners,
      pot: potDistribution.totalAwarded,
      communityCards: state.communityCards,
      potDistribution // Include detailed pot-by-pot distribution
    }
  }

  /**
   * Get available actions for a player (convenience method)
   */
  static getAvailableActions(state: GameState, playerId: string): ActionType[] {
    return getAvailableActions(state, playerId)
  }

  /**
   * Validate an action (convenience method)
   */
  static validateAction(state: GameState, playerId: string, action: PlayerAction) {
    return validateAction(state, playerId, action)
  }
}
